import type { AdminMfaEnrollmentResponse } from '@inflip/contracts/admin';
import { useState } from 'react';

import { OtpEnrollment } from '../components/otp-enrollment';
import { Button, ErrorBanner, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';

export function SignInPage() {
  const { setSession, notice } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<AdminMfaEnrollmentResponse | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const result = await api.signIn({ email, password, mfaCode: mfaCode || undefined });
      if ('mfaEnrollmentRequired' in result) {
        // 첫 로그인 — Authenticator 앱 등록 후 생성된 코드로 다시 로그인
        setEnrollment(result);
        setMfaCode('');
        setPending(false);
        return;
      }
      // 성공하면 화면이 바뀌므로 버튼은 막아 둔 채로 둔다
      setSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-muted/40 px-6">
      <div className="w-full max-w-sm rounded-xl bg-card p-8 shadow-card">
        <h1 className="text-2xl font-extrabold tracking-tight">
          Inflip <span className="text-sm font-semibold text-muted-foreground">ADMIN</span>
        </h1>
        <p className="mb-6 mt-1 text-sm text-muted-foreground">운영자 계정으로 로그인해주세요</p>
        {/* 2단계 인증을 켠 뒤 등 — 로그아웃된 이유 */}
        {notice && (
          <p role="status" className="mb-4 rounded-md bg-brand-soft px-4 py-3 text-sm text-brand">
            {notice}
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-2.5">
          <input
            type="email"
            autoComplete="email"
            className={`w-full ${inputCls}`}
            placeholder="어드민 이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            autoComplete="current-password"
            className={`w-full ${inputCls}`}
            placeholder="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            className={`w-full ${inputCls}`}
            placeholder="MFA 코드 (필요 시)"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? '로그인 중…' : '로그인'}
          </Button>
        </form>
        {error && (
          <div className="mt-3">
            <ErrorBanner message={error} />
          </div>
        )}
        {enrollment && (
          <div className="mt-4 space-y-2 rounded-md bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-bold">OTP 등록이 필요해요</p>
            <p>OTP 앱에 등록한 뒤, 앱에 보이는 6자리 코드를 입력하고 다시 로그인해주세요.</p>
            <div className="rounded-md bg-background p-3 text-foreground">
              <OtpEnrollment secret={enrollment.secret} otpauthUrl={enrollment.otpauthUrl} />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
